import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { ScrollableTableProps } from '../DataTable/DataTable.types';
import {
  ScrollShadowBottom,
  ScrollShadowTop,
  TableContainer,
  TableWrapper,
} from './ScrollableTable.styles';

export const ScrollableTable: React.FC<ScrollableTableProps> = ({
  children,
  maxHeight = 600,
  scrollable = true,
}) => {
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const [showTopShadow, setShowTopShadow] = useState(false);
  const [showBottomShadow, setShowBottomShadow] = useState(false);

  const handleScroll = useCallback(() => {
    const wrapper = tableWrapperRef.current;
    if (!wrapper || !scrollable) return;

    const { scrollTop, scrollHeight, clientHeight } = wrapper;

    setShowTopShadow(scrollTop > 0);
    setShowBottomShadow(scrollTop + clientHeight < scrollHeight - 1);
  }, [scrollable]);

  useEffect(() => {
    const wrapper = tableWrapperRef.current;
    if (!wrapper || !scrollable) return;

    // Initial check
    handleScroll();

    wrapper.addEventListener('scroll', handleScroll, { passive: true });

    // Also check on resize
    const resizeObserver = new ResizeObserver(handleScroll);
    resizeObserver.observe(wrapper);

    return () => {
      wrapper.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, [scrollable, handleScroll]);

  if (!scrollable) {
    return <>{children}</>;
  }

  return (
    <TableContainer>
      <ScrollShadowTop visible={showTopShadow} />
      <TableWrapper
        ref={tableWrapperRef}
        maxHeight={maxHeight}
        scrollable={scrollable}
      >
        {children}
      </TableWrapper>
      <ScrollShadowBottom visible={showBottomShadow} />
    </TableContainer>
  );
};
